import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Ensure import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { PriorityInsights } from '@components/interview/analysis-components/ProrityInsight';
import { Summary } from '@components/interview/analysis-components/Summary';
import { getQuoteText } from '@utils/representativeQuotes';
import AnalyticsIcon from '@mui/icons-material/Analytics'; // Example Icon for Resonance
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Resonant
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuoteIcon from '@mui/icons-material/FormatQuote';
import HighlightOffIcon from '@mui/icons-material/HighlightOff'; // Missed
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import React from 'react';
import { useParams } from 'react-router';
import { AnalysisLayout } from './layout';

// Helper function to safely parse JSON
const safeJsonParse = (data) => {
  if (typeof data === 'object' && data !== null) {
    return data; // Already an object
  }
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Error parsing results JSON:', error);
      return null; // Indicate parsing failure
    }
  }
  console.warn('Results data is not an object or string:', data);
  return null; // Indicate invalid type
};

// Helper function to get color based on reaction type
const getReactionColor = (reactionType) => {
  const colors = {
    emotional: 'secondary.main',
    cognitive: 'info.main',
    affirming: 'success.main',
    surprising: 'warning.light',
    skeptical: 'warning.dark',
    confused: 'grey.500',
    indifferent: 'grey.700',
    resistant: 'error.main',
    other: 'text.secondary',
  };
  return colors[reactionType] || 'default';
};

const ResonanceScanAnalysis = ({ item }) => {
  const { id } = useParams();
  if (!item || !item.results) {
    return <Alert severity="warning">Resonance Scan analysis data is missing or incomplete.</Alert>;
  }

  const results = safeJsonParse(item.results);

  if (!results) {
    return (
      <Alert severity="error">
        Failed to parse Resonance Scan analysis results. Please check the data format.
      </Alert>
    );
  }

  const {
    transcripts_analyzed = 0,
    total_resonant_messages = 0,
    total_missed_messages = 0,
    reaction_type_distribution = {},
    message_prevalence = [],
  } = results;

  const totalReactions = Object.values(reaction_type_distribution).reduce(
    (sum, count) => sum + count,
    0,
  );

  const resonantMessages = message_prevalence.filter((m) => m.classification === 'resonant');
  const missedMessages = message_prevalence.filter((m) => m.classification === 'missed');

  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Resonance Scan Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <AnalyticsIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Resonance Scan Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />{' '}
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary item={item} results={results} />
        {/* 3. Message Counts Card */}
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Message Overview
            </Typography>
            <Grid container spacing={2} textAlign="center">
              <Grid item xs={4}>
                <Typography variant="h5">{transcripts_analyzed}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Transcripts Analyzed
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h5" color="success.main">
                  {total_resonant_messages}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Resonant Messages
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h5" color="error.main">
                  {total_missed_messages}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Missed Messages
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {/* 4. Reaction Distribution Card */}
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Reaction Type Distribution
            </Typography>
            {totalReactions > 0 ? (
              <Box>
                {Object.entries(reaction_type_distribution)
                  .sort(([, countA], [, countB]) => countB - countA) // Sort by count descending
                  .map(([type, count]) => (
                    <Box key={type} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: 'capitalize', fontWeight: 'medium' }}
                        >
                          {type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {count} ({((count / totalReactions) * 100).toFixed(1)}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(count / totalReactions) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 5,
                          bgcolor: 'grey.300',
                          '& .MuiLinearProgress-bar': { bgcolor: getReactionColor(type) },
                        }}
                      />
                    </Box>
                  ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No reaction data available.
              </Typography>
            )}
          </CardContent>
        </Card>
        {/* 5. Message Prevalence Accordions */}
        <Card variant="outlined" sx={{ mt: 2 }}>
          {/* Resonant Messages */}
          <Accordion defaultExpanded variant="default">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <CheckCircleOutlineIcon sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="h6">Resonant Messages ({resonantMessages.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List disablePadding>
                {resonantMessages.length > 0 ? (
                  resonantMessages.map((msg, index) => (
                    <React.Fragment key={`resonant-${index}`}>
                      <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {msg.message}
                        </Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          <Chip
                            label={msg.reaction_type}
                            size="small"
                            sx={{
                              bgcolor: getReactionColor(msg.reaction_type),
                              color: '#fff', // Assuming light text on colored backgrounds
                              textTransform: 'capitalize',
                            }}
                          />
                          <Chip
                            label={`Prevalence: ${msg.prevalence}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        {msg.representative_quotes && msg.representative_quotes.length > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{ p: 1.5, mt: 1, bgcolor: 'grey.50', width: '100%' }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 1,
                                fontWeight: 'medium',
                              }}
                            >
                              <QuoteIcon fontSize="small" /> Representative Quotes:
                            </Typography>
                            <List dense disablePadding sx={{ pl: 2 }}>
                              {msg.representative_quotes.map((quote, qIndex) => (
                                <ListItem
                                  key={qIndex}
                                  sx={{
                                    display: 'list-item',
                                    listStyleType: 'disc',
                                    p: 0,
                                    mb: 0.5,
                                  }}
                                >
                                  <ListItemText
                                    primary={`"${getQuoteText(quote)}"`}
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      fontStyle: 'italic',
                                    }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Paper>
                        )}
                      </ListItem>
                      {index < resonantMessages.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No resonant messages identified." />
                  </ListItem>
                )}
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Missed Messages */}
          <Accordion variant="default">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <HighlightOffIcon sx={{ mr: 1, color: 'error.main' }} />
              <Typography variant="h6">Missed Messages ({missedMessages.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List disablePadding>
                {missedMessages.length > 0 ? (
                  missedMessages.map((msg, index) => (
                    <React.Fragment key={`missed-${index}`}>
                      <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {msg.message}
                        </Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          <Chip
                            label={msg.reaction_type}
                            size="small"
                            sx={{
                              bgcolor: getReactionColor(msg.reaction_type),
                              color: '#fff', // Assuming light text on colored backgrounds
                              textTransform: 'capitalize',
                            }}
                          />
                          <Chip
                            label={`Prevalence: ${msg.prevalence}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        {msg.representative_quotes && msg.representative_quotes.length > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{ p: 1.5, mt: 1, bgcolor: 'grey.50', width: '100%' }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 1,
                                fontWeight: 'medium',
                              }}
                            >
                              <QuoteIcon fontSize="small" /> Representative Quotes:
                            </Typography>
                            <List dense disablePadding sx={{ pl: 2 }}>
                              {msg.representative_quotes.map((quote, qIndex) => (
                                <ListItem
                                  key={qIndex}
                                  sx={{
                                    display: 'list-item',
                                    listStyleType: 'disc',
                                    p: 0,
                                    mb: 0.5,
                                  }}
                                >
                                  <ListItemText
                                    primary={`"${getQuoteText(quote)}"`}
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      fontStyle: 'italic',
                                    }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Paper>
                        )}
                      </ListItem>
                      {index < missedMessages.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No missed messages identified." />
                  </ListItem>
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        </Card>
        {/* 6. Priority Insights Card */}
        <PriorityInsights results={results} />
        {/* 7. Transcript List Accordion */}
        <IncludedTranscripts item={item} id={id} />
      </CardContent>
    </AnalysisLayout>
  );
};

export default ResonanceScanAnalysis;
